export interface AssetManifest {
  sprites: {
    worm: { idle: string; walk: string[]; jump: string };
    weapons: { grenade: string; bazooka: string };
    effects: { explosion: string[]; smoke: string[] };
    crosshair: string;
  };
  sounds: {
    weapons: { grenade_bounce: string; explosion: string; bazooka_fire: string };
    worm: { jump: string; damage: string; death: string };
    ui: { select: string; tick: string };
  };
  terrain: {
    default: string;
  };
}

export function generatePlaceholderSprite(
  width: number,
  height: number,
  color: string,
  label?: string,
): string {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d')!;

  ctx.fillStyle = color;
  ctx.fillRect(0, 0, width, height);

  ctx.strokeStyle = '#000';
  ctx.lineWidth = 1;
  ctx.strokeRect(0, 0, width, height);

  if (label) {
    ctx.fillStyle = '#000';
    ctx.font = `${Math.min(width, height) / 3}px monospace`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(label, width / 2, height / 2);
  }

  return canvas.toDataURL();
}

export function generateWormSprite(): string {
  const canvas = document.createElement('canvas');
  canvas.width = 16;
  canvas.height = 24;
  const ctx = canvas.getContext('2d')!;

  ctx.fillStyle = '#ff69b4';
  ctx.beginPath();
  ctx.ellipse(8, 16, 6, 10, 0, 0, Math.PI * 2);
  ctx.fill();

  ctx.fillStyle = '#fff';
  ctx.beginPath();
  ctx.arc(6, 10, 2, 0, Math.PI * 2);
  ctx.arc(10, 10, 2, 0, Math.PI * 2);
  ctx.fill();

  ctx.fillStyle = '#000';
  ctx.beginPath();
  ctx.arc(6, 10, 1, 0, Math.PI * 2);
  ctx.arc(10, 10, 1, 0, Math.PI * 2);
  ctx.fill();

  return canvas.toDataURL();
}

export function generateGrenadeSprite(): string {
  const canvas = document.createElement('canvas');
  canvas.width = 12;
  canvas.height = 16;
  const ctx = canvas.getContext('2d')!;

  ctx.fillStyle = '#2d5a27';
  ctx.beginPath();
  ctx.ellipse(6, 10, 5, 6, 0, 0, Math.PI * 2);
  ctx.fill();

  ctx.fillStyle = '#8b4513';
  ctx.fillRect(4, 2, 4, 4);

  ctx.fillStyle = '#ffd700';
  ctx.fillRect(5, 0, 2, 3);

  return canvas.toDataURL();
}

export function generateBazookaSprite(): string {
  const canvas = document.createElement('canvas');
  canvas.width = 32;
  canvas.height = 8;
  const ctx = canvas.getContext('2d')!;

  ctx.fillStyle = '#4a4a4a';
  ctx.fillRect(0, 2, 28, 4);

  ctx.fillStyle = '#2a2a2a';
  ctx.fillRect(24, 0, 8, 8);

  ctx.fillStyle = '#ff4500';
  ctx.beginPath();
  ctx.arc(4, 4, 3, 0, Math.PI * 2);
  ctx.fill();

  return canvas.toDataURL();
}

export function generateExplosionSprite(frame: number, maxFrames: number): string {
  const canvas = document.createElement('canvas');
  const size = 64;
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;

  const progress = frame / maxFrames;
  const radius = size * 0.4 * (1 - progress * 0.5);
  const alpha = 1 - progress;

  const gradient = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, radius);
  gradient.addColorStop(0, `rgba(255, 255, 200, ${alpha})`);
  gradient.addColorStop(0.3, `rgba(255, 200, 50, ${alpha})`);
  gradient.addColorStop(0.6, `rgba(255, 100, 0, ${alpha * 0.8})`);
  gradient.addColorStop(1, `rgba(100, 50, 0, 0)`);

  ctx.fillStyle = gradient;
  ctx.beginPath();
  ctx.arc(size / 2, size / 2, radius, 0, Math.PI * 2);
  ctx.fill();

  return canvas.toDataURL();
}

export function generateCrosshairSprite(): string {
  const canvas = document.createElement('canvas');
  canvas.width = 32;
  canvas.height = 32;
  const ctx = canvas.getContext('2d')!;

  ctx.strokeStyle = '#ff0000';
  ctx.lineWidth = 2;

  ctx.beginPath();
  ctx.moveTo(16, 0);
  ctx.lineTo(16, 12);
  ctx.moveTo(16, 20);
  ctx.lineTo(16, 32);
  ctx.moveTo(0, 16);
  ctx.lineTo(12, 16);
  ctx.moveTo(20, 16);
  ctx.lineTo(32, 16);
  ctx.stroke();

  ctx.beginPath();
  ctx.arc(16, 16, 8, 0, Math.PI * 2);
  ctx.stroke();

  return canvas.toDataURL();
}

export const placeholderManifest: AssetManifest = {
  sprites: {
    worm: {
      idle: generateWormSprite(),
      walk: [generateWormSprite(), generateWormSprite()],
      jump: generateWormSprite(),
    },
    weapons: {
      grenade: generateGrenadeSprite(),
      bazooka: generateBazookaSprite(),
    },
    effects: {
      explosion: Array.from({ length: 8 }, (_, i) => generateExplosionSprite(i, 8)),
      smoke: [generatePlaceholderSprite(16, 16, 'rgba(128,128,128,0.5)')],
    },
    crosshair: generateCrosshairSprite(),
  },
  sounds: {
    weapons: {
      grenade_bounce: '',
      explosion: '',
      bazooka_fire: '',
    },
    worm: {
      jump: '',
      damage: '',
      death: '',
    },
    ui: {
      select: '',
      tick: '',
    },
  },
  terrain: {
    default: '',
  },
};
